import React, { useState, useEffect, useRef } from "react";
import { useTheme } from '../../context/ThemeContext';
import Switch from 'react-switch';

function JSONViewer({ url, close, idx, label }) {
  const { darkMode } = useTheme();
  const [jsonContent, setJSONContent] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const jsonContainerRef = useRef(null);
  const [isPrettyView, setIsPrettyView] = useState(true);
  const [jsonType, setJsonType] = useState(null); // 'quizz' or 'notes'

  useEffect(() => {
    const fetchJSON = async () => {
      try {
        const response = await fetch(url);
        const json = await response.json();
        setJSONContent(json);
        // Detect JSON type
        if (json[0]?.question) {
          setJsonType('quizz');
        } else if (json[0]?.themes) {
          setJsonType('notes');
        }
      } catch (error) {
        console.error("Error fetching JSON:", error);
        setJSONContent(null); // Or set an error message
      }
    };

    fetchJSON();
  }, [url]);

  const handleSearchChange = (event) => {
    const term = event.target.value;
    setSearchTerm(term);
    setHighlightedIndex(-1);

    // Disable highlighting if search term is less than 3 characters
    if (term.length < 3) {
      setSearchTerm("");
    }
  };

  const highlightMatches = (text, term) => {
    if (!term) return text;
    return text
      .split(new RegExp(`(${term})`, "gi"))
      .map((part, i) => (i % 2 ? <mark key={i}>{part}</mark> : part));
  };

  const scrollToMatch = (index) => {
    if (jsonContainerRef.current) {
      const highlightedElements =
        jsonContainerRef.current.querySelectorAll("mark");
      if (highlightedElements[index]) {
        highlightedElements[index].scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    }
  };

  const handleNextMatch = () => {
    setHighlightedIndex((prevIndex) => {
      const nextIndex = prevIndex + 1;
      const highlightedElements =
        jsonContainerRef.current.querySelectorAll("mark");
      if (nextIndex < highlightedElements.length) {
        scrollToMatch(nextIndex);
        return nextIndex;
      }
      return prevIndex;
    });
  };

  const handlePreviousMatch = () => {
    setHighlightedIndex((prevIndex) => {
      const newIndex = prevIndex - 1;
      if (newIndex >= 0) {
        scrollToMatch(newIndex);
        return newIndex;
      }
      return 0;
    });
  };

  // Calculate number of matches
  const numberOfMatches = jsonContent
    ? JSON.stringify(jsonContent).split(new RegExp(`(${searchTerm})`, "gi"))
        .length - 1
    : 0;

  const filterJSON = (json) => {
    return JSON.stringify(jsonContent)
      .replaceAll("{", "")
      .replaceAll("}", "")
      .replaceAll("[", "")
      .replaceAll("]", "")
      .replaceAll('"', "")
      .replaceAll(":", "");
  };

  const renderPrettyQuizz = (data) => {
    return (
      <div className="content">
        <h3 className="title is-4 mb-4">Quiz Questions</h3>
        {data.map((item, index) => (
          <div key={item.id} className="box mb-4">
            <h4 className="title is-5">Question {index + 1}</h4>
            <p className="mb-3"><strong>Q:</strong> {item.question}</p>
            <div className="ml-4 mb-3">
              {item.options.map((option, idx) => (
                <p key={idx} className={`${item.correct_answer === String.fromCharCode(65 + idx) ? 'has-text-success has-text-weight-bold' : ''}`}>
                  {String.fromCharCode(65 + idx)}. {option}
                </p>
              ))}
            </div>
            <p className="has-text-info mb-2"><strong>Hint:</strong> {item.hint}</p>
            <p className="has-text-success"><strong>Explanation:</strong> {item.explanation}</p>
          </div>
        ))}
      </div>
    );
  };

  const renderPrettyNotes = (data) => {
    return (
      <div className="content">
        {data.map((section, idx) => (
          <div key={idx} className="mb-6">
            <h3 className="title is-4 mb-4">{section.title}</h3>
            {section.themes.map((theme, themeIdx) => (
              <div key={themeIdx} className="mb-5">
                <h4 className="title is-5">{theme.theme_label}</h4>
                <div className="ml-4">
                  {theme.flashcards.map((card, cardIdx) => (
                    <div key={cardIdx} className="box mb-3">
                      <p className="has-text-weight-bold">{card.concept}</p>
                      <p className="ml-3">{card.definition}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            <div className="notification is-info is-light">
              <p className="has-text-weight-bold">Content Summary:</p>
              <p>{section.content}</p>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className={`modal is-active is-rounded`}>
      <div className="modal-background" onClick={close} style={{
        opacity: '0.5',
        backgroundColor: 'rgba(0, 0, 0, 0.5)'
      }}></div>
      <div className={`modal-card is-rounded ${darkMode ? 'has-background-dark' : 'has-background-light'}`} style={{borderRadius: '15px'}}>
        <header className={`modal-card-head is-rounded ${darkMode ? 'has-background-grey-darker' : 'has-background-light'}`} style={{borderRadius: '15px'}}>
          <p className={`modal-card-title ${darkMode ? 'has-text-white' : 'has-text-dark'}`}>
            JSON Viewer - {label} (chap {idx * 1 + 1})
          </p>
          <button 
            className="delete" 
            aria-label="close" 
            onClick={close}
          ></button>
        </header>

        <section className={`modal-card-body is-rounded ${darkMode ? 'has-background-dark' : 'has-background-light'}`}>
        {!isPrettyView &&   <input
            type="text"
            placeholder="Search JSON..."
            value={searchTerm}
            className={`input mb-3 ${darkMode ? 'is-dark' : 'is-light'}`}
            onChange={handleSearchChange}
          />}
          
         {!isPrettyView &&  <div className="mb-3 is-flex is-justify-content-space-between">
                     <button
                       onClick={handlePreviousMatch}
                       disabled={highlightedIndex <= 0}
                       className={`button is-small ${darkMode ? 'is-dark' : 'is-light'}`}>
                       &lt;
                     </button>
                     <span className={darkMode ? 'has-text-white' : 'has-text-dark'}>
                       {highlightedIndex + 1} of {numberOfMatches} matches
                     </span>
                     <button
                       onClick={handleNextMatch}
                       disabled={highlightedIndex >= numberOfMatches - 1}
                       className={`button is-small ${darkMode ? 'is-dark' : 'is-light'}`}>
                       &gt;
                     </button>
                   </div>}

          {!isPrettyView && <div className="mb-3 is-flex is-justify-content-space-between">
            <span className={darkMode ? 'has-text-white' : 'has-text-dark'}>
              Char length: {filterJSON(jsonContent).length} - Words length: {filterJSON(jsonContent).split(" ").length}
            </span>
          </div>}

          <div className="mb-3 is-flex is-justify-content-end is-align-items-center">
         
            <Switch
              onChange={() => setIsPrettyView(!isPrettyView)}
              checked={isPrettyView}
              onColor="#86d3ff"
              onHandleColor="#2693e6"
              handleDiameter={24}
              uncheckedIcon={false}
              checkedIcon={false}
              height={20}
              width={48}
              className="react-switch"
            />
            <span className={`ml-3 subtitle is-5 ${darkMode ? 'has-text-white' : 'has-text-dark'}`}>
              {isPrettyView ? 'Pretty' : 'Raw JSON'}
            </span>
          </div>

          <div
            ref={jsonContainerRef}
            className={`${darkMode ? 'has-background-black' : 'has-background-white-ter'}`}
            style={{
              maxHeight: '60vh',
              maxWidth: '100%',
              overflow: 'auto',
              whiteSpace: 'pre-wrap',
              textWrap: 'wrap',
              lineBreak: 'anywhere',
              padding: '1rem',
              borderRadius: '4px'
            }}>
            {jsonContent && isPrettyView ? (
              <div className={darkMode ? 'has-text-white' : 'has-text-dark'}>
                {jsonType === 'quizz' && renderPrettyQuizz(jsonContent)}
                {jsonType === 'notes' && renderPrettyNotes(jsonContent)}
              </div>
            ) : (
              <pre
                style={{
                  maxWidth: '100%',
                  overflow: 'hidden',
                  whiteSpace: 'pre-wrap',
                  textWrap: 'wrap',
                  lineBreak: 'anywhere',
                }}
                className={darkMode ? 'has-text-white' : 'has-text-dark'}>
                {jsonContent
                  ? highlightMatches(JSON.stringify(jsonContent, null, 2), searchTerm)
                  : "Loading JSON..."}
              </pre>
            )}
          </div>
          </section>
      </div>
      
    </div>
  );
}

export default JSONViewer;
